import React, { useState, useEffect } from 'react';
import Card from '../../components/common/Card/Card';
import Button from '../../components/common/Button/Button';
import Modal from '../../components/common/Modal/Modal';
import LoadingSpinner from '../../components/common/LoadingSpinner/LoadingSpinner';
import { getMyDisputes, raiseDispute } from '../../services/disputeService';
import { getMyStudentProjects } from '../../services/studentService';
import { getClassesByProject } from '../../services/classService';
import { formatDate } from '../../utils/helpers';
import { useToast } from '../../context/ToastContext';
import useAuth from '../../hooks/useAuth';
import styles from '../common/Disputes.module.css';
import { sortProjectsByName } from '../../utils/sortProjects';

const StudentDisputes = () => {
  const [disputes, setDisputes] = useState([]);
  const [projects, setProjects] = useState([]);
  const [classes, setClasses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  
  const [projectId, setProjectId] = useState('');
  const [classId, setClassId] = useState('');
  const [disputeText, setDisputeText] = useState('');
  const [submitting, setSubmitting] = useState(false);
  
  const toast = useToast();
  const { user } = useAuth();

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const fetchData = async () => {
    try {
      if (!user) return;
      const [disputesData, projectsData] = await Promise.all([
        getMyDisputes(),
        getMyStudentProjects()
      ]);
      setDisputes(disputesData);
      setProjects(projectsData);
    } catch (error) {
      console.error('Error fetching disputes:', error);
      toast.error('Failed to load disputes');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (projectId) {
      fetchClasses();
    } else {
      setClasses([]);
      setClassId('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  const fetchClasses = async () => {
    try {
      const classesData = await getClassesByProject(projectId);
      setClasses(classesData);
    } catch (error) {
      console.error('Error fetching classes:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!projectId || !disputeText.trim()) {
      toast.warning('Please fill in all fields');
      return;
    }

    setSubmitting(true);
    try {
      await raiseDispute({
        project_id: projectId,
        class_id: classId || null,
        dispute: disputeText
      });
      toast.success('Dispute raised successfully');
      setShowModal(false);
      setProjectId('');
      setClassId('');
      setDisputeText('');
      fetchData();
    } catch (error) {
      toast.error('Failed to raise dispute: ' + error.message);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return <div className={styles.loading}><LoadingSpinner /></div>;
  }

  return (
    <div className={styles.disputesPage}>
      <div className={styles.header}>
        <h1>My Disputes</h1>
        <Button icon="+" onClick={() => setShowModal(true)}>Raise Dispute</Button>
      </div>

      <div className={styles.disputesList}>
        {disputes.length === 0 ? (
          <Card>
            <p className={styles.empty}>You haven't raised any disputes.</p>
          </Card>
        ) : (
          disputes.map(dispute => (
            <Card key={dispute.id} className={styles.disputeCard}>
              <div className={styles.disputeHeader}>
                <div className={styles.disputeMeta}>
                  <h3 className={styles.projectName}>{dispute.project_name}</h3>
                  <span className={styles.date}>{formatDate(dispute.created_at)}</span>
                </div>
                <span className={`${styles.status} ${dispute.addressed ? styles.statusAddressed : styles.statusPending}`}>
                  {dispute.addressed ? 'Addressed' : 'Pending Review'}
                </span>
              </div>
              <div className={styles.disputeBody}>
                {dispute.dispute}
              </div>
            </Card>
          ))
        )}
      </div>

      <Modal isOpen={showModal} onClose={() => setShowModal(false)} title="Raise a Dispute">
        <form onSubmit={handleSubmit} className={styles.form}>
          <div className={styles.formGroup}>
            <label>Select Project *</label>
            <select 
              value={projectId} 
              onChange={(e) => setProjectId(e.target.value)}
              required
            >
              <option value="">-- Select Project --</option>
              {sortProjectsByName(projects).map(p => (
                <option key={p.id} value={p.id}>{p.project_name}</option>
              ))}
            </select>
          </div>

          {projectId && (
            <div className={styles.formGroup}>
              <label>Select Class (Optional)</label>
              <select 
                value={classId} 
                onChange={(e) => setClassId(e.target.value)}
              >
                <option value="">-- Project Level Issue (No specific class) --</option>
                {classes.map(c => (
                  <option key={c.id} value={c.id}>
                    Class {c.class_number} - {formatDate(c.class_date)}
                  </option>
                ))}
              </select>
            </div>
          )}

          <div className={styles.formGroup}>
            <label>Describe the Issue *</label>
            <textarea 
              rows="5"
              value={disputeText}
              onChange={(e) => setDisputeText(e.target.value)}
              placeholder="Please describe the issue in detail..."
              required
            />
          </div>
          <div className={styles.formActions}>
            <Button type="button" variant="outline" onClick={() => setShowModal(false)}>Cancel</Button>
            <Button type="submit" loading={submitting}>Submit Dispute</Button>
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default StudentDisputes;
